// Utils
import { get } from 'lodash';

// Components
import SearchBar from 'shared/ui/components/form-components/SearchBar.vue';

export default {
    components: { SearchBar },

    data() {
        return {
            columns: [],
            fullCriteriaItems: undefined,
            fullItems: [],
            hasFullLocalData: true,
            items: [],
            searchTerm: '',
            showSearchBar: true,

            pagination: {
                totalRows: 0,
                searchTerm: undefined
            }
        };
    },

    watch: {
        searchTerm() {
            if (this.hasFullLocalData) {
                this.applyCriteriaLocally();
            } else {
                this.loadData();
            }
        }
    },

    methods: {
        /**
         * Applies search criteria locally.
         *
         * This method must be implemented in the extending component.
         *
         * @throws {Error} Throws an error if the method is not implemented in the extending component.
         */
        applyCriteriaLocally() {
            throw new Error('Method "applyCriteriaLocally" must be implemented in extending component.');
        },

        /**
         * Asynchronously loads data.
         *
         * This method must be implemented in the extending component.
         *
         * @throws {Error} Throws an error if the method is not implemented in the extending component.
         * @abstract
         */
        async loadData() {
            throw new Error('Method "loadData" must be implemented in extending component.');
        },

        /**
         * Applies the search criteria locally. If a search term is present,
         * it triggers the local search functionality.
         */
        applySearchCriteriaLocally() {
            const searched = !!this.searchTerm;
            if (searched) {
                this.localSearch();
            }
        },

        /**
         * Does a local search (used when all elements are here)
         */
        localSearch() {
            const items = [];
            for (const item of this.fullCriteriaItems ?? this.fullItems) {
                for (const column of this.columns) {
                    const value = get(item, column.field, '');
                    if (typeof value === 'string' &&
                        value.toLocaleLowerCase().includes(this.searchTerm.toLocaleLowerCase())) {
                        items.push(item);
                        break;
                    }
                }
            }
            this.fullCriteriaItems = items;
            this.total = items.length;
        },

        /**
         * Since we don't know how many total items there are when searching,
         * we request 1 more. If we get it, then we know there is another page.
         *
         * @param {number} baseLimit - this is the normal limit without search
         * @returns {number}
         */
        getLimitForSearchPagination(baseLimit) {
            return baseLimit + 1;
        },

        /**
         * @param {boolean} isSearching
         * @param {number} maxItemsPerPage - this is the normal `limit` set by the parent page
         * @param {number} skip
         */
        setPaginationFields(isSearching, maxItemsPerPage, skip) {
            if (!isSearching) {
                this.resetPagination();
                return;
            }

            // if the searchTerm has changed, we need to reset pagination
            if (this.searchTerm !== this.pagination.searchTerm) {
                this.resetPagination();
            }

            this.pagination.searchTerm = this.searchTerm;

            // we don't know what the actual total rows are, but we need to
            // tell the paginator to show the next button if there are more
            // pages. Since we are querying 1 extra row, we know there are
            // more pages if we get that extra row. So we can simply set the
            // totalRows to be the skip plus the length of items from the
            // query (remember that the length includes the extra row at this
            // point). When we delete the extra row from the items array, the
            // paginator will see that there is still 1 more page.
            const totalRows = skip + this.items.length;

            // remove the overflow item(s)
            this.items = this.items.slice(0, maxItemsPerPage);

            return totalRows;
        },

        resetPagination() {
            this.pagination = {
                totalRows: 0,
                searchTerm: undefined
            };
        }
    }
};
